import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Minus, Dices, X, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DICE = [
  { type: "d4", faces: 4, color: "#8b5cf6" },
  { type: "d6", faces: 6, color: "#3b82f6" },
  { type: "d8", faces: 8, color: "#10b981" },
  { type: "d10", faces: 10, color: "#f59e0b" },
  { type: "d12", faces: 12, color: "#ef4444" },
  { type: "d20", faces: 20, color: "#ec4899" },
  { type: "d100", faces: 100, color: "#6366f1" },
];

/* ---------- animated floating die that rolls across the screen ---------- */
const FloatingDie = ({ die, onDone }) => {
  const [phase, setPhase] = useState("rolling");
  const [displayNum, setDisplayNum] = useState(1);
  const faces = DICE.find((d) => d.type === die.die_type)?.faces || 20;
  const dieColor = DICE.find((d) => d.type === die.die_type)?.color || "#ec4899";

  // Random trajectory: start from bottom-right area, arc to a landing spot
  const trajectory = useRef({
    startX: 85 + Math.random() * 10,
    startY: 85 + Math.random() * 10,
    endX: 15 + Math.random() * 60,
    endY: 15 + Math.random() * 40,
  });

  const [pos, setPos] = useState({ x: trajectory.current.startX, y: trajectory.current.startY });

  // Animate number cycling while rolling
  useEffect(() => {
    if (phase !== "rolling") return;
    const iv = setInterval(() => setDisplayNum(Math.floor(Math.random() * faces) + 1), 50);
    const timeout = setTimeout(() => {
      clearInterval(iv);
      setDisplayNum(die.total);
      setPhase("result");
    }, 1400);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, [phase, faces, die.total]);

  // Animate position: travel across screen during rolling phase
  useEffect(() => {
    if (phase !== "rolling") return;
    const t = trajectory.current;
    const duration = 1400;
    const start = performance.now();
    let raf;
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic for smooth deceleration
      const ease = 1 - Math.pow(1 - progress, 3);
      // Arc: add a vertical bounce using sine
      const arcHeight = -25 * Math.sin(progress * Math.PI);
      setPos({
        x: t.startX + (t.endX - t.startX) * ease,
        y: t.startY + (t.endY - t.startY) * ease + arcHeight,
      });
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // Hold result, then fade
  useEffect(() => {
    if (phase !== "result") return;
    const t = setTimeout(() => setPhase("fade"), 2400);
    return () => clearTimeout(t);
  }, [phase]);

  // Remove after fade
  useEffect(() => {
    if (phase === "fade") {
      const t = setTimeout(onDone, 600);
      return () => clearTimeout(t);
    }
  }, [phase, onDone]);

  const rawResult = die.results?.[0] || die.total - die.modifier;
  const isNat20 = die.die_type === "d20" && rawResult === 20;
  const isNat1 = die.die_type === "d20" && rawResult === 1;

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        transform: "translate(-50%, -50%)",
        opacity: phase === "fade" ? 0 : 1,
        transition: phase === "fade" ? "opacity 0.6s ease-out" : undefined,
      }}
    >
      <div className="flex flex-col items-center gap-1">
        <div
          className="relative flex items-center justify-center rounded-xl shadow-2xl"
          style={{
            width: 72,
            height: 72,
            background: `linear-gradient(135deg, ${dieColor}, ${dieColor}cc)`,
            border: `3px solid ${dieColor}`,
            boxShadow: phase === "result"
              ? `0 0 40px ${dieColor}88, 0 0 80px ${dieColor}44, 0 8px 32px rgba(0,0,0,0.5)`
              : `0 0 20px ${dieColor}44, 0 8px 24px rgba(0,0,0,0.4)`,
            animation: phase === "rolling" ? "dice-tumble 0.15s linear infinite" : undefined,
            transform: phase === "result" ? "scale(1.15)" : undefined,
            transition: phase === "result" ? "transform 0.3s ease-out, box-shadow 0.3s ease-out" : undefined,
          }}
        >
          <span
            className="text-white font-black text-2xl tabular-nums drop-shadow-lg"
            style={{ fontFamily: "'Courier New', monospace" }}
          >
            {displayNum}
          </span>
        </div>
        <span className="text-[11px] font-bold text-white drop-shadow-lg bg-black/70 px-2 py-0.5 rounded-full whitespace-nowrap">
          {die.roller_name} &mdash; {die.die_type}
          {die.modifier > 0 ? `+${die.modifier}` : die.modifier < 0 ? die.modifier : ""}
        </span>
        {phase === "result" && (
          <span
            className={`text-sm font-black drop-shadow-lg px-3 py-1 rounded-full ${
              isNat20
                ? "bg-yellow-400 text-black animate-pulse"
                : isNat1
                ? "bg-red-600 text-white animate-pulse"
                : "bg-black/70 text-white"
            }`}
          >
            {isNat20 ? "NAT 20!" : isNat1 ? "NAT 1!" : `= ${die.total}`}
          </span>
        )}
        {die.label && phase === "result" && (
          <span className="text-[10px] text-white/90 bg-black/60 px-2 py-0.5 rounded-full">
            {die.label}
          </span>
        )}
      </div>
    </div>
  );
};

/* ---------- self-contained floating dice roller ---------- */
const DiceRoller = () => {
  const params = useParams();
  const campaignId = params.campaignId;

  const [open, setOpen] = useState(false);
  const [modifier, setModifier] = useState(0);
  const [label, setLabel] = useState("");
  const [rolls, setRolls] = useState([]);
  const [floatingDice, setFloatingDice] = useState([]);
  const seenRollIds = useRef(new Set());
  const initialFetched = useRef(false);

  // Detect guest vs DM mode
  const guestToken = campaignId ? localStorage.getItem(`guest_${campaignId}`) : null;
  const isGuest = !!guestToken;

  // API URLs based on mode
  const rollUrl = isGuest
    ? `${API}/guest/portal/${campaignId}/roll`
    : `${API}/campaigns/${campaignId}/roll`;
  const rollsUrl = isGuest
    ? `${API}/guest/portal/${campaignId}/rolls`
    : `${API}/campaigns/${campaignId}/rolls`;

  const getConfig = useCallback(() => {
    if (isGuest) return { headers: { "X-Guest-Token": guestToken, "Content-Type": "application/json" } };
    return { withCredentials: true, headers: { "Content-Type": "application/json" } };
  }, [isGuest, guestToken]);

  // Fetch initial rolls
  useEffect(() => {
    if (!campaignId || initialFetched.current) return;
    initialFetched.current = true;
    const fetchRolls = async () => {
      try {
        const res = await axios.get(rollsUrl, getConfig());
        setRolls(res.data);
        res.data.forEach((r) => seenRollIds.current.add(r.id));
      } catch { /* ignore */ }
    };
    fetchRolls();
  }, [campaignId, rollsUrl, getConfig]);

  // Poll for new rolls every 4s
  useEffect(() => {
    if (!campaignId) return;
    const interval = setInterval(async () => {
      try {
        const since = rolls[0]?.created_at;
        const url = since ? `${rollsUrl}?since=${encodeURIComponent(since)}` : rollsUrl;
        const res = await axios.get(url, getConfig());
        if (res.data.length > 0) {
          const fresh = res.data.filter((r) => !seenRollIds.current.has(r.id));
          fresh.forEach((r) => {
            seenRollIds.current.add(r.id);
            setFloatingDice((prev) => [...prev, r]);
          });
          setRolls((prev) => {
            const ids = new Set(prev.map((r) => r.id));
            const newRolls = res.data.filter((r) => !ids.has(r.id));
            return [...newRolls, ...prev].slice(0, 50);
          });
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(interval);
  }, [campaignId, rolls, rollsUrl, getConfig]);

  // Roll dice
  const handleRoll = async (dieType) => {
    try {
      const res = await axios.post(rollUrl, {
        die_type: dieType, die_count: 1, modifier, label,
      }, getConfig());
      seenRollIds.current.add(res.data.id);
      setFloatingDice((prev) => [...prev, res.data]);
      setRolls((prev) => [res.data, ...prev].slice(0, 50));
      setLabel("");
    } catch { /* ignore */ }
  };

  const removeFloating = useCallback((id) => {
    setFloatingDice((prev) => prev.filter((d) => d.id !== id));
  }, []);

  /* ---------- Draggable position (persisted in localStorage) ---------- */
  // Position is stored as an offset from bottom-right corner: { right: px, bottom: px }
  const DEFAULT_POS = { right: 20, bottom: 64 };
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dice-roller-pos") || "null");
      if (saved && typeof saved.right === "number" && typeof saved.bottom === "number") return saved;
    } catch { /* ignore */ }
    return DEFAULT_POS;
  });
  const dragState = useRef({ dragging: false, moved: false, startX: 0, startY: 0, startRight: 0, startBottom: 0 });

  const onDragStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    dragState.current = {
      dragging: true, moved: false,
      startX: point.clientX, startY: point.clientY,
      startRight: pos.right, startBottom: pos.bottom,
    };
  };
  const onDragMove = useCallback((e) => {
    if (!dragState.current.dragging) return;
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragState.current.startX;
    const dy = point.clientY - dragState.current.startY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragState.current.moved = true;
    // Right/bottom offsets INVERT the cursor delta (drag left → right offset increases)
    const nextRight  = Math.max(4, Math.min(window.innerWidth  - 60, dragState.current.startRight  - dx));
    const nextBottom = Math.max(4, Math.min(window.innerHeight - 60, dragState.current.startBottom - dy));
    setPos({ right: nextRight, bottom: nextBottom });
  }, []);
  const onDragEnd = useCallback(() => {
    if (!dragState.current.dragging) return;
    const wasMoved = dragState.current.moved;
    dragState.current.dragging = false;
    if (wasMoved) {
      try { localStorage.setItem("dice-roller-pos", JSON.stringify(pos)); } catch { /* ignore */ }
    }
  }, [pos]);

  useEffect(() => {
    const move = (e) => onDragMove(e);
    const end  = () => onDragEnd();
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", end);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("touchend", end);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", end);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("touchend", end);
    };
  }, [onDragMove, onDragEnd]);

  const handleToggleClick = () => {
    // If the pointer moved during "drag", swallow the click so we don't toggle the panel
    if (dragState.current.moved) { dragState.current.moved = false; return; }
    setOpen((o) => !o);
  };

  // Don't render if not on a campaign page
  if (!campaignId) return null;

  return (
    <>
      {/* Floating dice animations — always visible on top of everything */}
      {floatingDice.map((die) => (
        <FloatingDie key={die.id} die={die} onDone={() => removeFloating(die.id)} />
      ))}

      {/* Floating toggle button (draggable) */}
      <button
        onClick={handleToggleClick}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
        className="fixed z-[90] w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-transform duration-200 hover:scale-110 active:scale-95 group cursor-grab active:cursor-grabbing touch-none select-none"
        style={{
          right: `${pos.right}px`,
          bottom: `${pos.bottom}px`,
          background: open
            ? "linear-gradient(135deg, #1e1b4b, #312e81)"
            : "linear-gradient(135deg, #7c3aed, #a855f7)",
          boxShadow: open
            ? "0 4px 20px rgba(99,102,241,0.4)"
            : "0 4px 24px rgba(168,85,247,0.5), 0 0 40px rgba(168,85,247,0.15)",
        }}
        title="Click to open · drag to move"
        data-testid="dice-roller-toggle"
      >
        {open ? (
          <ChevronDown className="w-6 h-6 text-white pointer-events-none" />
        ) : (
          <Dices className="w-6 h-6 text-white group-hover:rotate-12 transition-transform pointer-events-none" />
        )}
      </button>

      {/* Dice roller panel — anchored above the toggle button */}
      {open && (
        <div
          className="fixed z-[90] w-80 rounded-xl border border-border/60 shadow-2xl overflow-hidden"
          style={{
            right: `${pos.right}px`,
            bottom: `${pos.bottom + 72}px`,
            background: "hsl(var(--card))",
            backdropFilter: "blur(16px)",
            animation: "dice-panel-in 0.2s ease-out",
          }}
          data-testid="dice-roller-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Dices className="w-4 h-4 text-primary" />
              <span className="text-sm font-serif font-semibold text-foreground">Dice Roller</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              data-testid="dice-roller-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-3" data-testid="dice-roller">
            {/* Dice buttons */}
            <div className="flex flex-wrap gap-2">
              {DICE.map((d) => (
                <button
                  key={d.type}
                  onClick={() => handleRoll(d.type)}
                  className="flex items-center justify-center w-[38px] h-[38px] rounded-lg font-bold text-[11px] text-white transition-all hover:scale-110 active:scale-90 hover:shadow-lg"
                  style={{
                    background: `linear-gradient(135deg, ${d.color}, ${d.color}bb)`,
                    boxShadow: `0 2px 8px ${d.color}44`,
                  }}
                  data-testid={`roll-${d.type}`}
                >
                  {d.type}
                </button>
              ))}
            </div>

            {/* Modifier + Label */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5 bg-muted/30 rounded-md border border-border px-1 shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setModifier((m) => m - 1)}
                  data-testid="mod-minus"
                >
                  <Minus className="w-3 h-3" />
                </Button>
                <span
                  className="text-sm font-mono w-8 text-center tabular-nums text-foreground"
                  data-testid="modifier-display"
                >
                  {modifier >= 0 ? `+${modifier}` : modifier}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setModifier((m) => m + 1)}
                  data-testid="mod-plus"
                >
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Label (STR check...)"
                className="bg-input border-border text-sm h-8 flex-1"
                data-testid="roll-label-input"
              />
            </div>

            {/* Roll history */}
            {rolls.length > 0 && (
              <div className="space-y-1 max-h-44 overflow-y-auto pr-1" data-testid="roll-history">
                {rolls.slice(0, 25).map((roll) => {
                  const rawResult = roll.results?.[0] || roll.total - roll.modifier;
                  const isNat20 = roll.die_type === "d20" && rawResult === 20;
                  const isNat1 = roll.die_type === "d20" && rawResult === 1;
                  return (
                    <div
                      key={roll.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs ${
                        isNat20
                          ? "bg-yellow-500/10 border border-yellow-500/30"
                          : isNat1
                          ? "bg-red-500/10 border border-red-500/30"
                          : "bg-muted/20"
                      }`}
                      data-testid={`roll-entry-${roll.id}`}
                    >
                      <Badge
                        className="text-[10px] shrink-0 border-none"
                        style={{
                          background: DICE.find((d) => d.type === roll.die_type)?.color || "#888",
                          color: "white",
                        }}
                      >
                        {roll.die_type}
                      </Badge>
                      <span className="font-medium text-foreground truncate">{roll.roller_name}</span>
                      <span className="text-muted-foreground">rolled</span>
                      <span
                        className={`font-bold tabular-nums ${
                          isNat20 ? "text-yellow-500" : isNat1 ? "text-red-500" : "text-foreground"
                        }`}
                      >
                        {roll.total}
                      </span>
                      {roll.modifier !== 0 && (
                        <span className="text-muted-foreground text-[10px]">
                          ({roll.results?.join("+")}
                          {roll.modifier > 0 ? `+${roll.modifier}` : roll.modifier})
                        </span>
                      )}
                      {roll.label && (
                        <span className="text-muted-foreground italic truncate ml-auto text-[10px]">
                          {roll.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes dice-tumble {
          0%   { transform: rotate(0deg) scale(1); }
          25%  { transform: rotate(22deg) scale(1.06); }
          50%  { transform: rotate(-18deg) scale(0.94); }
          75%  { transform: rotate(12deg) scale(1.03); }
          100% { transform: rotate(0deg) scale(1); }
        }
        @keyframes dice-panel-in {
          from { opacity: 0; transform: translateY(12px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};

export default DiceRoller;
